export function safeAudioUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      url.hostname === "cdn.subsplash.com" &&
      !url.username &&
      !url.password &&
      !url.port &&
      /^\/audios\/.+\.(mp3|m4a|aac|ogg)$/i.test(url.pathname)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function extractSubsplashAudio(
  html: string,
  code: string,
): string | null {
  const find = (node: any): string | null => {
    if (!node || typeof node !== "object") return null;
    if (node.short_code === code) {
      const outputs = node._embedded?.audio?._embedded?.["audio-outputs"];
      if (!Array.isArray(outputs)) return null;
      for (const output of outputs) {
        if (!/^audio\//.test(output.content_type || "")) continue;
        const url = safeAudioUrl(output._links?.related?.href);
        if (url) return url;
      }
      return null;
    }
    for (const child of Object.values(node)) {
      const found = find(child);
      if (found) return found;
    }
    return null;
  };
  for (const script of html.matchAll(/<script\b[^>]*>([\s\S]*?)<\/script>/gi)) {
    const push = script[1].match(
      /^\s*self\.__next_f\.push\(([\s\S]*)\)\s*;?\s*$/,
    );
    if (!push) continue;
    try {
      const chunk = JSON.parse(push[1]);
      if (chunk[0] !== 1 || typeof chunk[1] !== "string") continue;
      for (const line of chunk[1].split("\n")) {
        try {
          const found = find(JSON.parse(line.slice(line.indexOf(":") + 1)));
          if (found) return found;
        } catch {
          // Flight streams also contain references and non-JSON records.
        }
      }
    } catch {
      // Unsupported player markup keeps the original Subsplash embed.
    }
  }
  return null;
}

export async function withCompactAudio<
  T extends {
    type?: unknown;
    content?: unknown;
    compact_audio?: unknown;
  },
>(entry: T): Promise<T & { audio_url?: string }> {
  if (
    entry.type !== "media" ||
    !entry.compact_audio ||
    typeof entry.content !== "string"
  )
    return entry;
  try {
    const url = new URL(entry.content);
    const code = url.pathname.match(
      /^\/u\/[a-z0-9-]+\/media\/embed\/d\/([a-z0-9]+)\/?$/i,
    )?.[1];
    if (
      url.protocol !== "https:" ||
      url.hostname !== "subsplash.com" ||
      url.username ||
      url.password ||
      url.port ||
      !code
    )
      return entry;
    const response = await fetch(url.href, {
      redirect: "manual",
      signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) return entry;
    const html = await response.text();
    const audio =
      html.length <= 2_000_000 ? extractSubsplashAudio(html, code) : null;
    return audio ? { ...entry, audio_url: audio } : entry;
  } catch {
    return entry;
  }
}
